import React, { useState } from 'react';
import { useTheme } from '@material-ui/core/styles';
import { IconButton, Tooltip, useMediaQuery } from '@material-ui/core';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import moment from 'moment';

import Table from '../../UI/Table/Table';
import ImpresionDetalleModal from '../ImpresionDetalleModal/ImpresionDetalleModal';
import ImpresionPdfModal from '../ImpresionPdfModal/ImpresionPdfModal';
import ImpresionMailModal from '../ImpresionMailModal/ImpresionMailModal';
import ImpresionWhatsappModal from '../ImpresionWhatsappModal/ImpresionWhatsappModal';
import ComprobantesImpresionCards from './ComprobantesImpresionCards';
import { VERDE_WHATSAPP } from '../coloresWhatsapp';
import { formatNumberWithMonedaSigno } from '../../../assets/shared/formatNumber';

const cellStyles = {
  fontSize: 'small',
  paddingTop: '5px',
  paddingBottom: '5px',
  borderBottom: '0px'
};

const headerStyle = {
  fontSize: 'small',
  paddingTop: '0px',
  paddingBottom: '0px',
  borderBottom: '0px',
  backgroundColor: 'rgb(220 220 220)'
};

// Comprobantes cuyo PDF el motor de impresión no genera (la API los marca con soportaEnvio false).
const SIN_ENVIO = 'Este tipo de comprobante todavía no se puede imprimir ni enviar';

/**
 * Listado de comprobantes de ventas o de compras con las acciones de envío (PDF, mail y WhatsApp) y sus modales.
 * Lo usan Ver comprobantes (ventas y compras) y Pedidos; cada página decide cómo se muestra el Estado (renderEstado).
 * tituloTercero: "Cliente" (ventas) o "Proveedor" (compras).
 * En tablet se ocultan Núm. Externo y Referencia; en celular se muestran como tarjetas.
 */
const ComprobantesImpresionTable = ({ comprobantes, loading, renderEstado, tituloTercero = 'Cliente' }) => {
  const theme = useTheme();
  const celular = useMediaQuery(theme.breakpoints.down('xs'));
  const tablet = useMediaQuery(theme.breakpoints.only('sm'));
  const [comprobanteDetalle, setComprobanteDetalle] = useState(null);
  const [comprobanteSeleccionado, setComprobanteSeleccionado] = useState(null);
  const [comprobanteMail, setComprobanteMail] = useState(null);
  const [comprobanteWhatsapp, setComprobanteWhatsapp] = useState(null);

  const acciones = (rowData) => {
    const sinEnvio = rowData.soportaEnvio === false;
    // El span deja mostrar el tooltip aunque el botón esté deshabilitado.
    const accion = (titulo, onClick, icono) => (
      <Tooltip title={sinEnvio ? SIN_ENVIO : titulo}>
        <span>
          <IconButton size="small" disabled={sinEnvio} onClick={onClick}>
            {icono}
          </IconButton>
        </span>
      </Tooltip>
    );

    return (
      <React.Fragment>
        <Tooltip title="Ver detalle">
          <IconButton size="small" onClick={() => setComprobanteDetalle(rowData)}>
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {accion('Ver PDF', () => setComprobanteSeleccionado(rowData), <PictureAsPdfIcon fontSize="small" />)}
        {accion('Enviar por mail', () => setComprobanteMail(rowData), <MailOutlineIcon fontSize="small" />)}
        {accion(
          'Enviar por WhatsApp',
          () => setComprobanteWhatsapp(rowData),
          <WhatsAppIcon fontSize="small" style={sinEnvio ? undefined : { color: VERDE_WHATSAPP }} />
        )}
      </React.Fragment>
    );
  };

  const columns = [
    {
      title: 'Fecha',
      field: 'fecha',
      align: 'left',
      cellStyle: cellStyles,
      headerStyle: headerStyle,
      render: (rowData) => moment(rowData.fecha).format('DD/MM/YYYY')
    },
    {
      title: 'Comprobante',
      field: 'nombreComprobante',
      align: 'left',
      cellStyle: cellStyles,
      headerStyle: headerStyle,
      render: (rowData) => `${rowData.idComproba.trim()} - ${rowData.nombreComprobante ? rowData.nombreComprobante.trim() : ''}`
    },
    {
      title: 'Núm. Externo',
      field: 'nroExterno',
      align: 'left',
      hidden: tablet,
      cellStyle: cellStyles,
      headerStyle: headerStyle
    },
    {
      title: 'Referencia',
      field: 'nroReferencia',
      align: 'right',
      hidden: tablet,
      cellStyle: cellStyles,
      headerStyle: headerStyle
    },
    {
      title: tituloTercero,
      field: 'nombre',
      align: 'left',
      cellStyle: cellStyles,
      headerStyle: headerStyle,
      render: (rowData) => `${rowData.idCliente ? rowData.idCliente.trim() : ''} - ${rowData.nombre ? rowData.nombre.trim() : ''}`
    },
    {
      title: 'Total',
      field: 'total',
      align: 'right',
      cellStyle: cellStyles,
      headerStyle: headerStyle,
      render: (rowData) => formatNumberWithMonedaSigno(rowData.total)
    },
    {
      title: 'Estado',
      field: 'anulado',
      align: 'left',
      sorting: false,
      cellStyle: cellStyles,
      headerStyle: headerStyle,
      render: renderEstado
    },
    {
      title: 'Acciones',
      field: 'nroReferencia',
      align: 'center',
      sorting: false,
      cellStyle: { ...cellStyles, whiteSpace: 'nowrap' },
      headerStyle: headerStyle,
      render: acciones
    }
  ];

  return (
    <React.Fragment>
      {celular ? (
        <ComprobantesImpresionCards comprobantes={comprobantes} loading={loading} renderEstado={renderEstado} renderAcciones={acciones} />
      ) : (
        <Table
          title=""
          columns={columns}
          data={comprobantes}
          loading={loading}
          customOptions={{
            pageSize: 20,
            pageSizeOptions: [20, 50, 100],
            search: false,
            toolbar: false
          }}
        />
      )}

      <ImpresionDetalleModal comprobante={comprobanteDetalle} onClose={() => setComprobanteDetalle(null)} />
      <ImpresionPdfModal comprobante={comprobanteSeleccionado} onClose={() => setComprobanteSeleccionado(null)} />
      <ImpresionMailModal comprobante={comprobanteMail} onClose={() => setComprobanteMail(null)} />
      <ImpresionWhatsappModal comprobante={comprobanteWhatsapp} onClose={() => setComprobanteWhatsapp(null)} />
    </React.Fragment>
  );
};

export default ComprobantesImpresionTable;
